import { useState } from 'react'
import { Button, Badge, EmptyState, TextInput } from '../../shared/ui'
import { usePermissions } from '../../auth/usePermissions'
import { notify } from '../../store/notificationStore'
import { useSchulen, type School } from './api/schulenApi'
import { useLinkTour, useUnlinkTour } from './api/tourApi'
import { KitchenTourModal } from '../verwaltung/kostenstellen/components/KitchenTourModal'

const urlValid = (u: string) => {
  try { return new URL(u).protocol === 'https:' } catch { return false }
}

/**
 * "3D" tab body for the SchoolEditorModal: link a school to its self-hosted
 * virtual tour (a 3DVista export hosted on Azure Blob/CDN), show the linked
 * embed, and launch the immersive tour. Read-only users see the embed + "Take me
 * there"; write users (IT.Admin / school managers) can link/unlink by URL.
 *
 * Provider-agnostic — the panel just stores an https tour URL. No vendor API.
 */
export function SchoolTourPanel({ school }: { school: School }) {
  const { isAdmin, hasAnyRole } = usePermissions()
  const canManage = isAdmin || hasAnyRole('School.Manager', 'Operations.Manager', 'Verwaltung.Leitung', 'CEO')

  // The `school` prop is a snapshot from when the modal opened. Read the live
  // record from the schulen cache so link/unlink (which invalidate ['schulen'])
  // refresh this panel immediately instead of showing stale link state.
  const { data: schools } = useSchulen()
  const live = schools?.find((s) => s.id === school.id) ?? school
  const fmId = live.id ?? ''
  const linkedUrl = live.tourEmbedUrl
  const linkedName = live.tourName

  const [tourOpen, setTourOpen] = useState(false)
  const [editing, setEditing] = useState(false)      // re-link while already linked
  const [urlInput, setUrlInput] = useState('')
  const [nameInput, setNameInput] = useState('')

  const showForm = !linkedUrl || editing
  const link = useLinkTour()
  const unlink = useUnlinkTour()

  function doLink() {
    if (!fmId) { notify.error('Schule ohne ID — kann nicht verknüpft werden.'); return }
    const embedUrl = urlInput.trim()
    if (!urlValid(embedUrl)) { notify.error('Ungültige Tour-URL (https erforderlich).'); return }
    link.mutate(
      { id: fmId, embedUrl, tourName: nameInput.trim() || null },
      {
        onSuccess: () => {
          notify.success('3D-Tour verknüpft.')
          setEditing(false); setUrlInput(''); setNameInput('')
        },
        onError: (e: unknown) => notify.error(e instanceof Error ? e.message : 'Verknüpfen fehlgeschlagen.'),
      },
    )
  }

  function doUnlink() {
    if (!fmId) return
    unlink.mutate(fmId, {
      onSuccess: () => { setTourOpen(false); notify.success('3D-Verknüpfung entfernt.') },
      onError: (e: unknown) => notify.error(e instanceof Error ? e.message : 'Entfernen fehlgeschlagen.'),
    })
  }

  return (
    <>
      {/* ── Linked: embed + actions ─────────────────────────────────────────── */}
      {linkedUrl && !editing && (
        <div className="space-y-4">
          <div className="flex items-center gap-2 min-w-0">
            <Badge tone="success">Verknüpft</Badge>
            <span className="text-sm font-medium text-on-surface truncate">{linkedName || '3D-Tour'}</span>
          </div>
          <div className="rounded-2xl overflow-hidden border border-outline-variant bg-slate-950 aspect-video">
            <iframe
              title={`3D-Vorschau — ${school.name}`}
              src={linkedUrl}
              className="w-full h-full"
              style={{ border: 0 }}
              allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen"
              allowFullScreen
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" icon="tour" onClick={() => setTourOpen(true)}>Take me there</Button>
            {canManage && (
              <>
                <Button variant="ghost" icon="edit" onClick={() => { setEditing(true); setUrlInput(linkedUrl); setNameInput(linkedName ?? '') }}>Ändern</Button>
                <Button variant="ghost" icon="link_off" loading={unlink.isPending} onClick={doUnlink}>Entfernen</Button>
              </>
            )}
          </div>
          <p className="text-[11px] text-on-surface-variant">
            Die 3DVista-Tour muss öffentlich erreichbar sein (Azure Blob/CDN), damit die Einbettung lädt.
          </p>
        </div>
      )}

      {/* ── Form: paste the hosted tour URL ─────────────────────────────────── */}
      {showForm && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-primary text-base">view_in_ar</span>
            <h4 className="text-[9px] font-black uppercase tracking-widest text-primary">3D-Tour verknüpfen</h4>
          </div>

          {!canManage ? (
            <EmptyState
              icon="lock"
              title="Keine Berechtigung"
              description="Zum Verknüpfen einer 3D-Tour fehlt die Schreibberechtigung für das Modul Schulen."
            />
          ) : (
            <>
              <TextInput
                label="Tour-URL (3DVista, https)"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value.trim())}
                placeholder="https://…blob.core.windows.net/tours/…/index.htm"
              />
              <TextInput
                label="Bezeichnung (optional)"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                placeholder="z. B. Küche EG"
              />
              <p className="text-[11px] text-on-surface-variant">
                Exportiere die Tour aus 3DVista, lade sie nach Azure Blob/CDN, und füge hier die URL der <strong>index.htm</strong> ein.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="primary" icon="link" disabled={!urlValid(urlInput)} loading={link.isPending} onClick={doLink}>
                  Verknüpfen
                </Button>
                {linkedUrl && (
                  <Button variant="ghost" onClick={() => { setEditing(false); setUrlInput(''); setNameInput('') }}>Abbrechen</Button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <KitchenTourModal
        school={tourOpen && linkedUrl ? { id: fmId, name: school.name, embedUrl: linkedUrl, abkuerzung: live.abkuerzung } : null}
        onClose={() => setTourOpen(false)}
      />
    </>
  )
}
